import { useState } from 'preact/hooks'
import { useForm } from 'react-hook-form'
import { Download, Loader2, CloudUpload, AlertCircle, Trash2, Pencil, Check, X, LockOpen } from 'lucide-preact'
import type { WebAssetViewDto, AssetVersionViewDto, ApprovalCommentViewDto } from '@/api/asset'
import type { AssetReviewPermissions } from '@/hooks/useAssetReviewPermissions'
import { BRAND_CLASSES } from '@/constants/theme'
import { ASSET_TYPE_META, DEFAULT_ASSET_META, ASSET_STATUS_COLORS } from '../constants/assetMeta'
import { ConfirmationModal } from '@/components/modals/ConfirmationModal'
import { AssetCommentThread } from './AssetCommentThread'
import { UploadVersionModal } from '../modals/UploadVersionModal'
import { SignOffModal } from '../modals/SignOffModal'
import { shortAgo } from '@/utils/time'
import { TextBoxField } from '@/components'

interface StatusAction {
  label: string
  targetStatus: string
  signOff?: boolean
  variant: 'primary' | 'secondary'
}

const STATUS_ACTIONS: Record<string, StatusAction[]> = {
  'Ready for Sign-Off': [
    { label: 'Feedback Submitted', targetStatus: 'Feedback Submitted', variant: 'secondary' },
    { label: 'Sign Off', targetStatus: 'Signed Off', signOff: true, variant: 'primary' },
  ],
  'Reopened': [
    { label: 'Ready for Sign-Off', targetStatus: 'Ready for Sign-Off', variant: 'secondary' },
    { label: 'Feedback Submitted', targetStatus: 'Feedback Submitted', variant: 'secondary' },
    { label: 'Sign Off', targetStatus: 'Signed Off', signOff: true, variant: 'primary' },
  ],
  'Feedback Submitted': [
    { label: 'Ready for Sign-Off', targetStatus: 'Ready for Sign-Off', variant: 'secondary' },
    { label: 'Sign Off', targetStatus: 'Signed Off', signOff: true, variant: 'primary' },
  ],
}

const NAME_MAX_LENGTH = 255

interface AssetReviewSidePanelProps {
  assetDetail: WebAssetViewDto | null
  versions: AssetVersionViewDto[]
  comments: ApprovalCommentViewDto[]
  selectedVersionNumber: number
  onVersionChange: (versionNumber: number) => void
  onSignOff: (comment?: string) => Promise<void>
  onAddComment: (text: string, parentCommentId?: string) => Promise<void>
  onUploadVersion: (file: File, versionComment?: string) => Promise<void>
  onUploadVersionByCloudStorage: (fileId: string, fileName: string, mimeType: string, provider: string, versionComment?: string) => Promise<void>
  onStatusChange: (newStatus: string) => Promise<void>
  onNameChange: (newName: string) => Promise<void>
  onDeleteAsset: () => Promise<void>
  loading: boolean
  permissions: AssetReviewPermissions
}

function formatBytes(bytes: number): string {
  if (bytes === 0) return '0 B'
  const k = 1024
  const sizes = ['B', 'KB', 'MB', 'GB']
  const i = Math.floor(Math.log(bytes) / Math.log(k))
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${sizes[i]}`
}

function getVersionOptionLabel(v: AssetVersionViewDto): string {
  if (!v.UploadStatus || v.UploadStatus === 'Completed') {
    return `v${v.VersionNumber} — ${v.OriginalFileName || 'Unknown'} (${formatBytes(v.FileSizeBytes)})`
  }
  if (v.UploadStatus === 'Pending') return `v${v.VersionNumber} — Queued for download...`
  if (v.UploadStatus === 'Downloading') return `v${v.VersionNumber} — Downloading...`
  if (v.UploadStatus === 'Failed') return `v${v.VersionNumber} — Upload failed`
  return `v${v.VersionNumber} — ${v.UploadStatus}...`
}

export function AssetReviewSidePanel({
  assetDetail,
  versions,
  comments,
  selectedVersionNumber,
  onVersionChange,
  onSignOff,
  onAddComment,
  onUploadVersion,
  onUploadVersionByCloudStorage,
  onStatusChange,
  onNameChange,
  onDeleteAsset,
  loading,
  permissions,
}: AssetReviewSidePanelProps) {
  const [showSignOffModal, setShowSignOffModal] = useState(false)
  const [showUploadModal, setShowUploadModal] = useState(false)
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [isDeleting, setIsDeleting] = useState(false)
  const [updatingTargetStatus, setUpdatingTargetStatus] = useState<string | null>(null)

  // Inline name editing
  const [isEditingName, setIsEditingName] = useState(false)
  const [isSavingName, setIsSavingName] = useState(false)

  const {
    control,
    handleSubmit: handleNameSubmit,
    formState: { isValid: isNameValid },
    reset: resetNameForm,
  } = useForm<{ Name: string }>({
    mode: 'onChange',
    defaultValues: { Name: '' },
  })

  if (!assetDetail) {
    return (
      <div className="flex items-center justify-center h-full">
        <Loader2 className="w-6 h-6 text-slate-400 animate-spin" />
      </div>
    )
  }

  const typeMeta = ASSET_TYPE_META[assetDetail.Type] || DEFAULT_ASSET_META
  const TypeIcon = typeMeta.Icon
  const statusColors = ASSET_STATUS_COLORS[assetDetail.Status] || BRAND_CLASSES.STATUS.DEFAULT
  const isSignedOff = assetDetail.Status === 'Signed Off'
  const allActions = STATUS_ACTIONS[assetDetail.Status] || []

  // Filter status actions based on permissions
  const currentActions = allActions.filter(action => {
    if (action.signOff) return permissions.canSignOff
    return permissions.canChangeStatus
  })

  const commentsDisabled = permissions.isCommentsDisabled(isSignedOff)

  const selectedVersion = versions.find(v => v.VersionNumber === selectedVersionNumber)
  const selectedUploadStatus = selectedVersion?.UploadStatus
  const isSelectedVersionProcessing = selectedUploadStatus === 'Pending' || selectedUploadStatus === 'Downloading'
  const isSelectedVersionFailed = selectedUploadStatus === 'Failed'

  // --- Name editing helpers ---

  const startEditingName = () => {
    if (isSignedOff || !permissions.canEditName) return
    resetNameForm({ Name: assetDetail.Name })
    setIsEditingName(true)
  }

  const cancelEditingName = () => {
    setIsEditingName(false)
    resetNameForm({ Name: '' })
  }

  const saveEditedName = handleNameSubmit(async (data) => {
    const trimmed = data.Name.trim()
    if (trimmed === assetDetail.Name) {
      cancelEditingName()
      return
    }
    setIsSavingName(true)
    try {
      await onNameChange(trimmed)
      setIsEditingName(false)
      resetNameForm({ Name: '' })
    } catch (error) {
      console.error('Failed to save asset name:', error)
    } finally {
      setIsSavingName(false)
    }
  })

  const handleNameKeyDown = (e: KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      saveEditedName()
    } else if (e.key === 'Escape') {
      e.preventDefault()
      cancelEditingName()
    }
  }

  return (
    <div className="flex flex-col h-full overflow-hidden">
      {/* Header + Details */}
      <div className="flex-shrink-0 p-4 border-b border-slate-200 dark:border-slate-700 space-y-3">
        {/* Title + actions */}
        <div className="flex items-start justify-between gap-2">
          {isEditingName ? (
            <div className="min-w-0 flex-1">
              <div className="flex items-start gap-1">
                <TextBoxField
                  control={control}
                  name="Name"
                  placeholder="Enter asset name"
                  autoFocus
                  disabled={isSavingName}
                  maxLength={NAME_MAX_LENGTH}
                  onKeyDown={handleNameKeyDown}
                  containerClassName="flex-1 min-w-0"
                  className="!py-1 !text-base !font-semibold"
                  rules={{
                    required: 'Asset name is required',
                    validate: value => {
                      const trimmed = (value ?? '').trim()
                      if (trimmed.length === 0) return 'Asset name is required'
                      if (trimmed.length > NAME_MAX_LENGTH) return `Asset name must be ${NAME_MAX_LENGTH} characters or less`
                      return true
                    }
                  }}
                />
                <button
                  type="button"
                  onClick={saveEditedName}
                  disabled={isSavingName || !isNameValid}
                  className="inline-flex items-center justify-center w-7 h-7 mt-0.5 rounded-md text-green-600 hover:bg-green-50 dark:text-green-400 dark:hover:bg-green-900/20 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                  title="Save"
                >
                  {isSavingName ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />}
                </button>
                <button
                  type="button"
                  onClick={cancelEditingName}
                  disabled={isSavingName}
                  className="inline-flex items-center justify-center w-7 h-7 mt-0.5 rounded-md text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors disabled:opacity-60"
                  title="Cancel"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>
          ) : (
            <div className="group flex items-start gap-1 min-w-0 flex-1">
              <h2
                className={`text-base font-semibold text-slate-900 dark:text-slate-100 leading-tight break-words min-w-0 transition-colors ${!isSignedOff && permissions.canEditName ? 'cursor-pointer hover:text-primary-600 dark:hover:text-primary-400' : 'cursor-default'}`}
                onClick={startEditingName}
                title={!permissions.canEditName ? undefined : isSignedOff ? 'Reopen to edit name' : 'Click to edit name'}
              >
                {assetDetail.Name}
              </h2>
              {!isSignedOff && permissions.canEditName && (
                <button
                  type="button"
                  onClick={startEditingName}
                  className="flex-shrink-0 inline-flex items-center justify-center w-6 h-6 rounded-md text-slate-400 opacity-0 group-hover:opacity-100 hover:text-slate-600 dark:hover:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 transition-all mt-0.5"
                  title="Edit name"
                >
                  <Pencil className="w-3 h-3" />
                </button>
              )}
            </div>
          )}
          <div className="flex-shrink-0 flex items-center gap-0.5">
            {assetDetail.CurrentVersionBlobUrl && (
              <a
                href={assetDetail.CurrentVersionBlobUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center w-8 h-8 rounded-md text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
                title="Download"
              >
                <Download className="w-4 h-4" />
              </a>
            )}
            {permissions.canDeleteAsset && (
              <button
                type="button"
                onClick={() => setShowDeleteModal(true)}
                disabled={isSignedOff}
                className="inline-flex items-center justify-center w-8 h-8 rounded-md text-slate-400 hover:text-red-600 dark:hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                title={isSignedOff ? 'Reopen to delete asset' : 'Delete asset'}
              >
                <Trash2 className="w-4 h-4" />
              </button>
            )}
          </div>
        </div>

        {/* Badges */}
        <div className="flex items-center gap-2 flex-wrap">
          <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${typeMeta.badgeBg} ${typeMeta.color}`}>
            <TypeIcon className="w-3 h-3" />
            {assetDetail.Type}
          </span>
          <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium whitespace-nowrap ${statusColors}`}>
            {assetDetail.Status}
          </span>
          {isSignedOff && permissions.canReopen && (
            <button
              onClick={async () => {
                setUpdatingTargetStatus('Reopened')
                try {
                  await onStatusChange('Reopened')
                } catch (error) {
                  console.error('Failed to update asset status:', error)
                } finally {
                  setUpdatingTargetStatus(null)
                }
              }}
              disabled={updatingTargetStatus !== null}
              className="ml-auto inline-flex items-center justify-center w-7 h-7 rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
              title="Reopen"
            >
              {updatingTargetStatus === 'Reopened' ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <LockOpen className="w-3.5 h-3.5" />}
            </button>
          )}
        </div>

        {/* Status Actions */}
        {!isSignedOff && currentActions.length > 0 && (
          <div className="flex gap-2">
            {currentActions.map((action) => (
              <button
                key={action.targetStatus}
                onClick={async () => {
                  if (action.signOff) {
                    if (!permissions.requireSignOffConfirmation) {
                      setUpdatingTargetStatus(action.targetStatus)
                      try {
                        await onSignOff()
                      } finally {
                        setUpdatingTargetStatus(null)
                      }
                    } else {
                      setShowSignOffModal(true)
                    }
                    return
                  }
                  setUpdatingTargetStatus(action.targetStatus)
                  try {
                    await onStatusChange(action.targetStatus)
                  } catch (error) {
                    console.error('Failed to update asset status:', error)
                  } finally {
                    setUpdatingTargetStatus(null)
                  }
                }}
                disabled={updatingTargetStatus !== null}
                className={`flex-1 inline-flex items-center justify-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed ${
                  action.variant === 'primary'
                    ? BRAND_CLASSES.BUTTON.PRIMARY
                    : 'border border-slate-300 dark:border-slate-600 text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800'
                }`}
              >
                {updatingTargetStatus === action.targetStatus ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                {action.label}
              </button>
            ))}
          </div>
        )}

        {/* Version Selector */}
        <div>
          <label className="block text-[11px] font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide mb-1">
            Version
          </label>
          <div className="flex gap-1.5">
            <select
              value={selectedVersionNumber}
              onChange={(e) => onVersionChange(Number((e.target as HTMLSelectElement).value))}
              className="flex-1 min-w-0 px-2.5 py-1.5 rounded-md border text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100"
            >
            {versions
              .slice()
              .sort((a, b) => b.VersionNumber - a.VersionNumber)
              .map((v) => (
                <option key={v.Id} value={v.VersionNumber}>
                  {getVersionOptionLabel(v)}
                </option>
              ))}
            </select>
            {permissions.canUploadVersion && (
              <button
                onClick={() => setShowUploadModal(true)}
                disabled={isSignedOff}
                className="flex-shrink-0 inline-flex items-center justify-center w-8 h-8 rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:opacity-40 disabled:cursor-not-allowed"
                title={isSignedOff ? 'Reopen to upload a new version' : 'Upload new version'}
              >
                <CloudUpload className="w-4 h-4" />
              </button>
            )}
          </div>

          {/* Version upload status indicator */}
          {isSelectedVersionProcessing && (
            <div className="flex items-center gap-1.5 mt-1.5">
              <Loader2 className="w-3 h-3 text-primary-500 animate-spin flex-shrink-0" />
              <p className="text-[11px] text-primary-600 dark:text-primary-400">
                Processing upload...
              </p>
            </div>
          )}

          {isSelectedVersionFailed && (
            <div className="flex items-start gap-1.5 mt-1.5">
              <AlertCircle className="w-3 h-3 text-red-500 flex-shrink-0 mt-0.5" />
              <p className="text-[11px] text-red-600 dark:text-red-400">
                {selectedVersion?.UploadErrorMessage || 'Upload failed'}
              </p>
            </div>
          )}

          {selectedVersion && !isSelectedVersionProcessing && !isSelectedVersionFailed && (
            <p className="text-[11px] text-slate-400 dark:text-slate-500 mt-1">
              Uploaded {shortAgo(selectedVersion.UploadedAt)} by {selectedVersion.UploadedBy}
            </p>
          )}
        </div>
      </div>

      {/* Comments */}
      <div className="flex-1 overflow-hidden flex flex-col min-h-0">
        <AssetCommentThread
          comments={comments}
          onAddComment={onAddComment}
          loading={loading}
          disabled={commentsDisabled}
        />
      </div>

      {/* Sign Off Modal */}
      <SignOffModal
        isOpen={showSignOffModal}
        onClose={() => setShowSignOffModal(false)}
        assetDetail={assetDetail}
        onSignOff={onSignOff}
      />

      {/* Upload Version Modal */}
      <UploadVersionModal
        isOpen={showUploadModal}
        onClose={() => setShowUploadModal(false)}
        assetDetail={assetDetail}
        onUploadVersion={onUploadVersion}
        onUploadVersionByCloudStorage={onUploadVersionByCloudStorage}
      />

      {/* Delete Confirmation Modal */}
      <ConfirmationModal
        isOpen={showDeleteModal}
        title="Delete Asset"
        message={`Are you sure you want to delete '${assetDetail.Name}'? This action cannot be undone.`}
        type="danger"
        confirmText="Delete"
        loading={isDeleting}
        onCancel={() => setShowDeleteModal(false)}
        onConfirm={async () => {
          setIsDeleting(true)
          try {
            await onDeleteAsset()
          } finally {
            setIsDeleting(false)
            setShowDeleteModal(false)
          }
        }}
      />
    </div>
  )
}
